'use client'

import { useExperiment } from '@/hooks/use-experiment'
import { getVisitorId } from '@/hooks/use-visitor'
import { trackEvent } from '@/lib/analytics'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TrackedCta } from '@/components/analytics/tracked-cta'
import { useTranslations } from 'next-intl'

interface HeroConfig {
  headline: string
  headlineAccent: string
  subheadline: string
  subheadline2: string
  ctaText: string
}

/**
 * Client component for the homepage hero copy.
 * If an experiment is active for the "hero" section on "/",
 * it renders the experiment variant's config. Otherwise, renders defaults.
 */
export function HeroCopy() {
  const t = useTranslations('home')
  const { variantConfig, experimentId, variant } = useExperiment('hero')

  const DEFAULT_CONFIG: HeroConfig = {
    headline: t('heroDefaultHeadline'),
    headlineAccent: t('heroDefaultAccent'),
    subheadline: t('heroDefaultSubheadline'),
    subheadline2: t('heroDefaultSubheadline2'),
    ctaText: t('heroDefaultCta'),
  }

  const config: HeroConfig = variantConfig
    ? { ...DEFAULT_CONFIG, ...(variantConfig as Partial<HeroConfig>) }
    : DEFAULT_CONFIG

  function handleCtaClick() {
    if (!experimentId || !variant) return

    const visitorId = getVisitorId()
    fetch('/api/analytics/experiment-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        experimentId,
        visitorId,
        variant,
        eventType: 'click_cta',
      }),
    }).catch(() => {})

    trackEvent('experiment_cta_click', {
      experiment_id: experimentId,
      variant,
    })
  }

  return (
    <>
      <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
        {config.headline}
        <span className="block bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(236,72,153,0.5)]">
          {config.headlineAccent}
        </span>
      </h1>

      <p className="text-xl text-zinc-300 mb-4 max-w-2xl mx-auto drop-shadow-lg">
        {config.subheadline}
      </p>

      <p className="text-lg text-zinc-400 mb-10 max-w-xl mx-auto">
        {config.subheadline2}
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
        <TrackedCta href="/signup" eventType="cta" location="hero" buttonText={config.ctaText}>
          <Button
            size="lg"
            className="text-lg px-8 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-shadow"
            onClick={handleCtaClick}
          >
            {config.ctaText}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </TrackedCta>
      </div>
      <div className="mb-12">
        <TrackedCta href="/demo" eventType="demo" location="hero_performer">
          <span className="text-sm text-zinc-400 hover:text-violet-300 transition-colors">
            {t('heroDemoLink')}
          </span>
        </TrackedCta>
      </div>
    </>
  )
}
